import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { SideNavComponent } from './shared/components/side-nav/side-nav.component';
import { HeaderComponent } from './shared/components/header/header.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    SideNavComponent,
    HeaderComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  isLoggedIn = false;
  currentUrl = '';
  title = '';
  isMobileOrTablet = false;
  sidenavOpened = false;
  translationsLoaded = false;

  private authService = inject(AuthService);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  ngOnInit(): void {
    // Session hydration AND the initial language's translations are both handled by
    // APP_INITIALIZER in app.config.ts before this runs (in parallel, via Promise.all) -
    // so translations are guaranteed loaded by the time AppComponent exists at all.
    this.translationsLoaded = true;

    this.breakpointObserver.observe([`(max-width: 600px)`]).subscribe(result => {
      this.isMobileOrTablet = result.matches;
      this.cdr.detectChanges();
    });

    // Subscribe to login state; use detectChanges() so the shell *ngIf switches
    // synchronously — this prevents the login page rendering inside the sidenav
    // for the brief window between navigation and the next change detection cycle.
    this.authService.isLoggedIn$.subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
      this.cdr.detectChanges();
    });

    const initialUrl = this.router.url;
    this.currentUrl = initialUrl;
    this.title = this.getTitleFromPath(initialUrl);

    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
      map((event: any) => event.urlAfterRedirects || event.url),
      distinctUntilChanged()
    ).subscribe((url: string) => {
      this.currentUrl = url;
      this.title = this.getTitleFromPath(url);
    });

  }

  openSidenav() { this.sidenavOpened = true; }
  closeSidenav() { this.sidenavOpened = false; }

  skipToMainContent(event: Event, mainContent: HTMLElement): void {
    event.preventDefault();
    mainContent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    this.focusPageStart(mainContent);
  }

  onRouteActivate(mainContent: HTMLElement): void {
    setTimeout(() => this.focusPageStart(mainContent));
  }

  private focusPageStart(mainContent: HTMLElement): void {
    const pageTitle = mainContent.querySelector<HTMLElement>('[data-page-title], h1');
    if (pageTitle) {
      pageTitle.tabIndex = -1;
    }
    (pageTitle ?? mainContent).focus({ preventScroll: true });
  }

  getTitleFromPath(path: string): string {
    if (!path || path === '/' || path.startsWith('/dashboard')) return 'sideNav.dashboard';
    if (path.startsWith('/patients')) return 'sideNav.patients';
    if (path.startsWith('/concentrators')) return 'sideNav.concentrators';
    if (path.startsWith('/device-alerts')) return 'sideNav.alerts';
    if (path.startsWith('/devices')) return 'sideNav.devices';
    if (path.startsWith('/providers')) return 'sideNav.providers';
    if (path.startsWith('/organizations')) return 'sideNav.Organizations';
    if (path.startsWith('/serviceCenters')) return 'sideNav.serviceCenters';
    if (path.startsWith('/admin/language')) return 'sideNav.language';
    if (path.startsWith('/operations')) return 'sideNav.operations';
    if (path.startsWith('/people/users')) return 'sideNav.users';
    if (path.startsWith('/login')) return 'app.login';
    return 'sideNav.dashboard';
  }
}
